const _excluded = ["children"],
  _excluded2 = ["className", "textFieldProps", "loading", "disableEndAdornment", "virtualizeOptions", "lazyOptions", "disableSelectedHighlight"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState } from 'react';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core';
import { TextField } from "../TextField";
import typography from "../../foundations/typography.module.scss";
import colors from "../../foundations/colors.module.scss";
import { ArrowDownFill } from "../../icons";
import { Spinner } from "../Spinner";
import { VariableSizeList } from 'react-window';
import compact from 'lodash/compact';

// popup options virtualization implemented based on https://v4.mui.com/components/autocomplete/#virtualization
const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const {
    data,
    index,
    style
  } = props;
  return /*#__PURE__*/React.cloneElement(data[index], {
    style: _objectSpread(_objectSpread({}, style), {}, {
      top: style.top + LISTBOX_PADDING
    })
  });
}
const OuterElementContext = /*#__PURE__*/React.createContext({});
const OuterElementType = /*#__PURE__*/React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, props, outerProps));
});
function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = /*#__PURE__*/React.forwardRef(function ListboxComponent(props, ref) {
  const {
      children
    } = props,
    other = _objectWithoutProperties(props, _excluded);
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;
  const getChildSize = child => {
    var _props;
    const isTwoLiner = compact(child === null || child === void 0 || (_props = child.props) === null || _props === void 0 ? void 0 : _props.children.props.children).length === 2;
    return isTwoLiner ? 48 : 36;
  };
  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };
  const gridRef = useResetCache(itemCount);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref
  }, /*#__PURE__*/React.createElement(OuterElementContext.Provider, {
    value: other
  }, /*#__PURE__*/React.createElement(VariableSizeList, {
    itemData: itemData,
    height: getHeight() + 2 * LISTBOX_PADDING,
    width: "100%",
    ref: gridRef,
    outerElementType: OuterElementType,
    innerElementType: "ul",
    itemSize: index => getChildSize(itemData[index]),
    overscanCount: 5,
    itemCount: itemCount
  }, renderRow)));
});
const useAutocompleteStyles = makeStyles({
  option: _ref => {
    let {
      disableSelectedHighlight
    } = _ref;
    return {
      fontFamily: typography.fontFamilyLight,
      fontSize: typography.fontSizeP1,
      lineHeight: typography.lineHeightP1,
      color: colors.dark900,
      '&[data-focus="true"]': {
        backgroundColor: disableSelectedHighlight ? "".concat(colors.dark70, " !important") : colors.dark70
      },
      '&[aria-selected="true"]': {
        backgroundColor: disableSelectedHighlight ? 'inherit' : colors.dark120
      }
    };
  },
  endAdornment: {
    paddingRight: 8
  },
  popper: {
    marginBottom: '4px'
  },
  inputRoot: _ref2 => {
    let {
      hasMultipleChips
    } = _ref2;
    return {
      flexWrap: 'wrap',
      paddingTop: hasMultipleChips ? 4 : undefined,
      '&.MuiOutlinedInput-adornedEnd': {
        paddingRight: '52px !important'
      },
      '& input': {
        width: 0,
        minWidth: 40,
        flexGrow: 1
      }
    };
  }
});
export function Autocomplete(_ref3) {
  let {
      className,
      textFieldProps,
      loading,
      disableEndAdornment,
      virtualizeOptions,
      lazyOptions,
      disableSelectedHighlight
    } = _ref3,
    props = _objectWithoutProperties(_ref3, _excluded2);
  const hasMultipleChips = !!props.multiple && Array.isArray(props.value) && props.value.length > 0;
  const classes = useAutocompleteStyles({
    hasMultipleChips,
    disableSelectedHighlight
  });
  const [isFocused, setIsFocused] = useState(false);
  if (virtualizeOptions) {
    props.ListboxComponent = ListboxComponent;
    props.options = isFocused && lazyOptions != null ? lazyOptions() : props.options;
  }
  return /*#__PURE__*/React.createElement(MuiAutocomplete, _extends({
    className: className,
    classes: {
      option: classes.option,
      endAdornment: classes.endAdornment,
      popper: classes.popper
    },
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({
      inputRootClass: classes.inputRoot
    }, textFieldProps, params, {
      testid: props['data-testid'] && "".concat(props['data-testid'], "-textfield"),
      suffix: !disableEndAdornment && /*#__PURE__*/React.createElement("span", {
        style: {
          marginBlockStart: 2,
          marginInlineEnd: 24
        }
      }, loading ? /*#__PURE__*/React.createElement(Spinner, {
        "data-testid": "".concat(props['data-testid'], "-spinner"),
        size: 32
      }) : null, params.InputProps.endAdornment)
    })),
    popupIcon: /*#__PURE__*/React.createElement(ArrowDownFill, null),
    ListboxProps: {
      'data-testid': "".concat(props['data-testid'], "-listbox")
    },
    loading: loading
  }, props, {
    onFocus: ev => {
      var _props$onFocus;
      setIsFocused(true);
      (_props$onFocus = props.onFocus) === null || _props$onFocus === void 0 || _props$onFocus.call(props, ev);
    },
    onBlur: ev => {
      var _props$onBlur;
      setIsFocused(false);
      (_props$onBlur = props.onBlur) === null || _props$onBlur === void 0 || _props$onBlur.call(props, ev);
    }
  }));
}